'use client';

import { addDays, format, startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import {
  adjustColorSaturation,
  getZoneColor,
  normalizeWorkoutSteps,
  parseWorkoutData,
} from '@trainme/api/client';
import { trpc } from '@/app/api/trpc/client';
import type {
  ActivityWithRelations,
  WorkoutWithRelations,
} from '@/utils/types';
import { cn } from '@/utils/uiUtils';

type Metric = 'time' | 'distance';
type ZoneMetrics = { time: number; distance: number };
type ZoneSegment = ZoneMetrics & { zone: number };
type TrainingZone = {
  zone: number;
  minValue: number | null;
  maxValue: number | null;
  type?: string | null;
};

const ZONES = [1, 2, 3, 4, 5, 6];

const toNumber = (value: unknown) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const addZoneMetrics = (
  map: Map<number, ZoneMetrics>,
  zone: number,
  time: number,
  distance: number
) => {
  if (time <= 0 && distance <= 0) return;
  const current = map.get(zone) ?? { time: 0, distance: 0 };
  map.set(zone, {
    time: current.time + Math.max(0, time),
    distance: current.distance + Math.max(0, distance),
  });
};

const zoneForHeartRate = (heartRate: number, zones: TrainingZone[]) => {
  const matchingZone = zones.find(
    (zone) =>
      heartRate >= (zone.minValue ?? 0) &&
      heartRate <= (zone.maxValue ?? Number.POSITIVE_INFINITY)
  );
  return matchingZone?.zone ?? zones[0]?.zone ?? 1;
};

const activityZones = (
  activity: ActivityWithRelations,
  zones: TrainingZone[]
): ZoneSegment[] => {
  const record = activity as unknown as Record<string, unknown>;
  const result = new Map<number, ZoneMetrics>();
  const laps = Array.isArray(record.laps) ? record.laps : [];
  let usedLaps = false;

  for (const rawLap of laps) {
    const lap = rawLap as Record<string, unknown>;
    const heartRate = toNumber(lap.averageHeartrate ?? lap.average_heartrate);
    const time = toNumber(
      lap.movingTime ?? lap.moving_time ?? lap.elapsedTime ?? lap.elapsed_time
    );
    const distance = toNumber(
      lap.distance ??
        lap.distanceMeters ??
        lap.totalDistance ??
        lap.elapsedDistance
    );
    if (heartRate && (time || distance)) {
      addZoneMetrics(
        result,
        zoneForHeartRate(heartRate, zones),
        time ?? 0,
        distance ?? 0
      );
      usedLaps = true;
    }
  }

  if (!usedLaps) {
    const heartRate = toNumber(
      record.averageHeartrate ?? record.average_heartrate
    );
    const time = toNumber(
      record.movingTime ??
        record.moving_time ??
        record.elapsedTime ??
        record.elapsed_time
    );
    const distance = toNumber(
      record.distance ??
        record.distanceMeters ??
        record.totalDistance ??
        record.elapsedDistance
    );
    if (heartRate && (time || distance)) {
      addZoneMetrics(
        result,
        zoneForHeartRate(heartRate, zones),
        time ?? 0,
        distance ?? 0
      );
    }
  }

  return Array.from(result, ([zone, metrics]) => ({ zone, ...metrics }));
};

const workoutZones = (workout: WorkoutWithRelations): ZoneSegment[] => {
  const steps = normalizeWorkoutSteps(workout.steps);
  const parsed = parseWorkoutData(steps, workout.sportType ?? 'Run');
  const result = new Map<number, ZoneMetrics>();

  for (let index = 1; index < parsed.length - 1; index += 1) {
    const current = parsed[index];
    const next = parsed[index + 1];
    const time = next.time - current.time;
    if (time > 0 && current.zone > 0) {
      addZoneMetrics(result, current.zone, time, 0);
    }
  }

  return Array.from(result, ([zone, metrics]) => ({ zone, ...metrics }));
};

const aggregateByDay = (
  items: Array<{ date: Date; zones: ZoneSegment[] }>,
  weekStart: Date
) => {
  const days = Array.from({ length: 7 }, (_, index) => ({
    date: addDays(weekStart, index),
    zones: new Map<number, ZoneMetrics>(),
  }));

  for (const item of items) {
    const day = days.find(
      (candidate) =>
        startOfDay(candidate.date).getTime() === startOfDay(item.date).getTime()
    );
    if (!day) continue;
    for (const segment of item.zones) {
      addZoneMetrics(day.zones, segment.zone, segment.time, segment.distance);
    }
  }

  return days.map((day) =>
    Array.from(day.zones, ([zone, metrics]) => ({ zone, ...metrics }))
  );
};

const getMetric = (segment: ZoneSegment, metric: Metric) =>
  metric === 'time' ? segment.time : segment.distance;

export function WeeklyZonesChart({
  activities,
  workouts,
  weekStart,
  isUserReady,
}: {
  activities: ActivityWithRelations[];
  workouts: WorkoutWithRelations[];
  weekStart: Date;
  isUserReady: boolean;
}) {
  const [metric, setMetric] = useState<Metric>('time');
  const zonesQuery = trpc.settings.getTrainingZones.useQuery(undefined, {
    enabled: isUserReady,
  });
  const heartRateZones = useMemo(() => {
    const zones = ((zonesQuery.data ?? []) as TrainingZone[])
      .filter((zone) => !zone.type || zone.type === 'HEART_RATE')
      .sort((a, b) => a.zone - b.zone);
    return zones.length
      ? zones
      : [{ zone: 1, minValue: 0, maxValue: Number.POSITIVE_INFINITY }];
  }, [zonesQuery.data]);
  const activitySegments = useMemo(
    () =>
      aggregateByDay(
        activities.flatMap((activity) =>
          activity.startDate
            ? [
                {
                  date: new Date(activity.startDate),
                  zones: activityZones(activity, heartRateZones),
                },
              ]
            : []
        ),
        weekStart
      ),
    [activities, heartRateZones, weekStart]
  );
  const workoutSegments = useMemo(
    () =>
      aggregateByDay(
        workouts.flatMap((workout) =>
          workout.date
            ? [{ date: new Date(workout.date), zones: workoutZones(workout) }]
            : []
        ),
        weekStart
      ),
    [weekStart, workouts]
  );
  const maxMetric = Math.max(
    1,
    ...activitySegments.flatMap((segments) =>
      segments.map((segment) => getMetric(segment, metric))
    ),
    ...workoutSegments.flatMap((segments) =>
      segments.map((segment) => getMetric(segment, metric))
    )
  );

  return (
    <>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400'>
          <span>
            <span className='mr-1 inline-block h-2 w-2 rounded-sm bg-slate-500' />
            Workout
          </span>
          <span>
            <span className='mr-1 inline-block h-2 w-2 rounded-sm bg-emerald-500' />
            Activity
          </span>
        </div>
        <div className='flex overflow-hidden rounded-full border border-emerald-500/70 text-xs font-bold uppercase tracking-[0.2em]'>
          {(['time', 'distance'] as const).map((option) => (
            <button
              key={option}
              type='button'
              onClick={() => setMetric(option)}
              className={cn(
                'px-4 py-2 transition',
                metric === option
                  ? 'bg-emerald-500 text-white'
                  : 'text-slate-500 dark:text-slate-300'
              )}
            >
              {option === 'time' ? 'Time' : 'Dist'}
            </button>
          ))}
        </div>
      </div>
      <div className='mt-5 grid h-52 grid-cols-7 gap-1 border-b border-slate-300 px-1 dark:border-slate-700 sm:gap-3 sm:px-2'>
        {Array.from({ length: 7 }, (_, index) => {
          const workoutDay = workoutSegments[index] ?? [];
          const activityDay = activitySegments[index] ?? [];
          return (
            <div
              key={index}
              className='flex h-full min-w-0 flex-col items-center justify-end'
            >
              <div className='flex h-44 w-full items-end justify-center gap-0.5 sm:gap-1'>
                <ZoneBar
                  segments={workoutDay}
                  metric={metric}
                  maxMetric={maxMetric}
                  workout
                />
                <ZoneBar
                  segments={activityDay}
                  metric={metric}
                  maxMetric={maxMetric}
                />
              </div>
              <span className='pb-2 pt-1 text-xs font-semibold text-slate-500 dark:text-slate-400'>
                {format(addDays(weekStart, index), 'EEEEE')}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ZoneBar({
  segments,
  metric,
  maxMetric,
  workout = false,
}: {
  segments: ZoneSegment[];
  metric: Metric;
  maxMetric: number;
  workout?: boolean;
}) {
  const values = new Map(segments.map((segment) => [segment.zone, segment]));
  const total = segments.reduce(
    (sum, segment) => sum + getMetric(segment, metric),
    0
  );
  const height = total > 0 ? (total / maxMetric) * 100 : 0;

  return (
    <div className='flex h-full w-[42%] max-w-10 items-end'>
      <div
        className='flex w-full flex-col-reverse overflow-hidden rounded-t-sm'
        style={{ height: `${height}%` }}
        title={`${workout ? 'Workout' : 'Activity'}: ${Math.round(total)} ${metric === 'time' ? 'seconds' : 'meters'}`}
      >
        {ZONES.map((zone) => {
          const segment = values.get(zone);
          const value = segment ? getMetric(segment, metric) : 0;
          return (
            <div
              key={zone}
              className='min-h-0 flex-1'
              style={{
                flexGrow: value,
                backgroundColor: workout
                  ? adjustColorSaturation(getZoneColor(zone), 0.7, 0.7)
                  : getZoneColor(zone),
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
